import { queryOptions } from "@tanstack/react-query";
import type {
  ProvenanceV2EntryBase,
  TextAnalyzeModelName,
  UnknownProvenanceModel,
} from "@/lib/types/provenance";
import type { PaginatedResponse, PaginationParams } from "@/lib/types";
import type { AllServices } from "@/lib/types/history";
import { api } from "@/lib/http";

export type HistoryListEntry<
  TModel extends
    | TextAnalyzeModelName
    | UnknownProvenanceModel = UnknownProvenanceModel,
  TPayload = unknown,
> = ProvenanceV2EntryBase<TModel, TPayload> & {
  has_children: boolean;
};
export async function getHistoryList(
  pagination: PaginationParams,
  models?: Array<AllServices>,
) {
  const searchParams = new URLSearchParams({
    page: pagination.page.toString(),
    per_page: pagination.page_size.toString(),
  });
  if (models) models.forEach((m) => searchParams.append("model_names", m));

  return api.detector
    .get<PaginatedResponse<HistoryListEntry>>("v2/ai-detector/user-history/", {
      searchParams,
    })
    .json();
}

export type HistoryDetailEntry<
  TModel extends
    | TextAnalyzeModelName
    | UnknownProvenanceModel = UnknownProvenanceModel,
  TPayload = unknown,
> = ProvenanceV2EntryBase<TModel, TPayload> & {
  children: Array<ProvenanceV2EntryBase<UnknownProvenanceModel>>;
};

export function getHistoryDetail<
  TModel extends
    | TextAnalyzeModelName
    | UnknownProvenanceModel = UnknownProvenanceModel,
  TPayload = unknown,
>(id: string) {
  return api.detector
    .get<
      HistoryDetailEntry<TModel, TPayload>
    >(`v2/ai-detector/user-history/${id}`)
    .json();
}

const historyQueryKey = ["detector", "history", "v3"] as const;
export function historyListQueryOptions(
  pagination: PaginationParams,
  models?: Array<AllServices>,
) {
  return queryOptions({
    queryKey: [...historyQueryKey, { pagination, models }],
    async queryFn({ client }) {
      const history = await getHistoryList(pagination, models);
      history.items.forEach((entry) => {
        // since we don't have children in the list, only prepopulate the childless
        if (!entry.has_children)
          client.setQueryData(
            historyDetailQueryOptions(entry.history_id).queryKey,
            { ...entry, children: [] },
          );
      });
      return history;
    },
  });
}

export function historyDetailQueryOptions<
  TModel extends
    | TextAnalyzeModelName
    | UnknownProvenanceModel = UnknownProvenanceModel,
  TPayload = unknown,
>(id: string) {
  return queryOptions({
    queryKey: [...historyQueryKey, id],
    queryFn: () => getHistoryDetail<TModel, TPayload>(id),
  });
}
